import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { isDarkAtom } from '../shared/uiAtoms';

/**
 * 背景类型
 */
export type BackgroundType = 'solid' | 'image' | 'video';

/**
 * 背景媒体类型
 */
export type BackgroundMediaType = 'image' | 'video';

/**
 * 单个主题模式的背景配置
 */
export interface BackgroundModeConfig {
  path: string; // 媒体文件路径
  objectPosition?: string; // 显示位置
  needsCrossfadeLoop?: boolean; // 是否需要交叉叠化循环
}

/**
 * 可用的背景配置
 */
export interface BackgroundImage {
  id: string;
  name: string;
  mediaType: BackgroundMediaType; // 媒体类型：图片或视频
  preview?: string; // 预览图路径（可选）
  light: BackgroundModeConfig; // 日间模式配置
  dark: BackgroundModeConfig; // 夜间模式配置
}

/**
 * 可用背景图片列表
 * 支持动态扩展，未来可以从服务器获取
 */
export const availableBackgroundsAtom = atom<BackgroundImage[]>([
  {
    id: 'tg',
    name: 'TG',
    mediaType: 'image',
    light: {
      path: '/bg/tg-light.png',
      objectPosition: 'center center'
    },
    dark: {
      path: '/bg/tg-dark.png',
      objectPosition: 'center center'
    }
  },
  {
    id: 'doodle',
    name: '涂鸦风格',
    mediaType: 'image',
    light: {
      path: '/bg/tg-doodle-light.jpg',
      objectPosition: 'center center'
    },
    dark: {
      path: '/bg/tg-doodle-dark.jpg',
      objectPosition: 'center center'
    }
  },
  {
    id: 'cat-art',
    name: '猫咪艺术',
    mediaType: 'image',
    light: {
      path: '/bg/tg-cat-draw-light.jpg',
      objectPosition: 'center center'
    },
    dark: {
      path: '/bg/digital-art-cat-pattern-dark.jpg',
      objectPosition: 'center center'
    }
  },
  {
    id: 'catgirl',
    name: '猫娘',
    mediaType: 'image',
    light: {
      path: '/bg/catgirl-bg-light.png',
      objectPosition: 'center center'
    },
    dark: {
      path: '/bg/catgirl-bg-dark.png',
      objectPosition: 'center center'
    }
  },
  {
    id: 'v-catgirl-1',
    name: '猫娘视频',
    mediaType: 'video',
    light: {
      path: '/bg/v-catgirl-1-light.mp4',
      objectPosition: '75% center', // 日间模式显示右边 1/4 位置
      needsCrossfadeLoop: true
    },
    dark: {
      path: '/bg/v-catgirl-1-dark.mp4',
      objectPosition: 'center center', // 夜间模式居中显示
      needsCrossfadeLoop: true
    }
  },
]);

/**
 * 背景类型设置 (纯色/图片)
 */
export const backgroundTypeAtom = atomWithStorage<BackgroundType>('v3_chat_background_type', 'solid');

/**
 * 日间模式背景图片ID
 */
export const lightBackgroundImageAtom = atomWithStorage<string>('v3_chat_light_background', 'catgirl');

/**
 * 夜间模式背景图片ID
 */
export const darkBackgroundImageAtom = atomWithStorage<string>('v3_chat_dark_background', 'catgirl');

/**
 * AI消息透明度 (0-100)
 */
export const messageOpacityAtom = atomWithStorage<number>('v3_chat_message_opacity', 70);

/**
 * Task Group透明度 (0-100)
 */
export const taskGroupOpacityAtom = atomWithStorage<number>('v3_chat_task_group_opacity', 50);

/**
 * 派生原子：获取当前主题下的背景图片
 */
export const currentBackgroundImageAtom = atom<BackgroundImage | null>((get) => {
  const backgrounds = get(availableBackgroundsAtom);
  const lightImageId = get(lightBackgroundImageAtom);
  const darkImageId = get(darkBackgroundImageAtom);

  // 这里简化处理，实际应该根据当前主题选择
  // 可以通过导入主题相关的atom来获取当前主题
  const currentImageId = lightImageId; // 暂时使用日间模式

  return backgrounds.find(bg => bg.id === currentImageId) || backgrounds[0] || null;
});

/**
 * 派生原子：根据主题获取背景媒体路径
 */
export const backgroundImagePathAtom = atom<string | null>((get) => {
  const backgroundType = get(backgroundTypeAtom);
  if (backgroundType === 'solid') return null;

  const backgrounds = get(availableBackgroundsAtom);
  const lightImageId = get(lightBackgroundImageAtom);
  const darkImageId = get(darkBackgroundImageAtom);
  const isDark = get(isDarkAtom);

  const currentImageId = isDark ? darkImageId : lightImageId;

  const background = backgrounds.find(bg => bg.id === currentImageId);
  if (!background) return null;

  return isDark ? background.dark.path : background.light.path;
});

/**
 * 派生原子：获取当前背景的媒体类型
 */
export const backgroundMediaTypeAtom = atom<BackgroundMediaType | null>((get) => {
  const backgroundType = get(backgroundTypeAtom);
  if (backgroundType === 'solid') return null;

  const backgrounds = get(availableBackgroundsAtom);
  const lightImageId = get(lightBackgroundImageAtom);
  const darkImageId = get(darkBackgroundImageAtom);
  const isDark = get(isDarkAtom);

  const currentImageId = isDark ? darkImageId : lightImageId;

  const background = backgrounds.find(bg => bg.id === currentImageId);
  return background?.mediaType || null;
});

/**
 * 派生原子：获取当前背景的对象位置
 */
export const backgroundObjectPositionAtom = atom<string | null>((get) => {
  const backgroundType = get(backgroundTypeAtom);
  if (backgroundType === 'solid') return null;

  const backgrounds = get(availableBackgroundsAtom);
  const lightImageId = get(lightBackgroundImageAtom);
  const darkImageId = get(darkBackgroundImageAtom);
  const isDark = get(isDarkAtom);

  const currentImageId = isDark ? darkImageId : lightImageId;

  const background = backgrounds.find(bg => bg.id === currentImageId);
  if (!background) return null;

  const modeConfig = isDark ? background.dark : background.light;
  return modeConfig.objectPosition || null;
});

/**
 * 派生原子：获取当前背景是否需要交叉叠化循环
 */
export const backgroundNeedsCrossfadeLoopAtom = atom<boolean>((get) => {
  const backgroundType = get(backgroundTypeAtom);
  if (backgroundType === 'solid') return false;

  const backgrounds = get(availableBackgroundsAtom);
  const lightImageId = get(lightBackgroundImageAtom);
  const darkImageId = get(darkBackgroundImageAtom);
  const isDark = get(isDarkAtom);

  const currentImageId = isDark ? darkImageId : lightImageId;

  const background = backgrounds.find(bg => bg.id === currentImageId);
  if (!background) return false;

  const modeConfig = isDark ? background.dark : background.light;
  return modeConfig.needsCrossfadeLoop || false;
});
